<template>
    <div class="position-relative">
        <template v-if="!isTemplatesActive">
            <div v-if="loading" class="loading-overlay">
                <b-spinner label="Загрузка..."></b-spinner>
            </div>        
    
            <div class="d-flex align-items-center justify-content-space-between mb-3">
                <div><h5 class="mb-0">Услуги компании</h5></div>
                <div>
                    <b-button @click="updateTemplateActive(true)" variant="secondary">Шаблоны счетов</b-button>
                    <b-button @click="newService" variant="success" class="ml-1">Добавить услугу</b-button>
                </div>
            </div>
            
            <b-table 
                :items="services" 
                :fields="fields" 
                bordered 
                responsive 
                show-empty
                :caption-top="false"
            >
                <template #empty="scope">
                    <div>Не создано ни одной услуги для этой компании. Создайте, нажав «Добавить услугу».</div>
                </template>

                <template #cell(name)="row">
                    <div>{{ row.item.name }}</div>
                    <div class="text-muted small">
                        <span
                            v-for="(column, index) in formatServiceColumns(row.item.columns)"
                            :key="index"
                        >
                            <div>{{ column.name }} -> {{ column.default }}{{ index < row.item.columns.length - 1 ? ';' : '' }}</div>
                        </span>
                    </div>
                </template>

                <template #cell(actions)="row">
                    <div class="text-center" style="width: 124px;">
                        <b-button 
                            @click="editService(row.item)" 
                            variant="table"
                            size="sm"
                        >
                            <i 
                                aria-label="Редактировать"
                                class="fas fa-pencil-alt"
                            ></i>
                        </b-button>
                        <b-button 
                            @click="cloneService(row.item)" 
                            variant="table"
                            size="sm"
                            class="ml-1"
                        >
                            <i 
                                aria-label="Копировать"
                                class="fas fa-copy text-secondary"
                            ></i>
                        </b-button>                     
                        <b-button 
                            @click="deleteService(row.item.id)" 
                            variant="table"
                            size="sm"
                            class="ml-1"
                        >
                            <i 
                                aria-label="Удалить"
                                class="fas fa-trash text-danger"
                            ></i>
                        </b-button>    
                    </div>
                </template>
            </b-table>
            <b-modal 
                v-model="showModal" 
                :title="form.id ? `Редактирование услуги #${form.id}`:`Создание услуги`" 
                size="xl"
                centered
                dialog-class="modal-upravlenie-uslugoy"
            >
                <b-form @submit.prevent="saveService">
                    <b-form-group style="width: 100%; overflow-x: auto;">
                        <div class="d-flex align-items-center justify-content-space-between mb-1">
                            <div style="font-size: 14px;">
                                Создайте разметку колонок для услуги в счёте.
                            </div>
                            <div>
                                <b-button 
                                    v-b-toggle.collapse-help 
                                    class="mr-1"
                                    size="sm"
                                >
                                    <b-icon
                                        icon="question-circle-fill"
                                        scale="1.3"
                                    ></b-icon>
                                </b-button>
                                <b-button 
                                    @click="addColumn" 
                                    variant="secondary" 
                                    size="sm"
                                >
                                    Добавить колонку
                                </b-button>          
                            </div>
                        </div>

                        <div class="mb-2">
                            <b-collapse id="collapse-help">
                                <b-card>
                                  <h5 style="text-transform: none;">Помощь</h5>
                                  <p class="mb-1">Форматы колонок:</p>
                                  <ul style="list-style: disc; margin-left: 15px; line-height: 16px;">
                                      <li class="mb-1"><b>Строка</b> - просто текст, </li>
                                      <li class="mb-1"><b>Число</b> - любое число, </li>
                                      <li class="mb-1"><b>Дата</b> - при работе будет предложен ввод значения через календарь,</li>
                                      <li class="mb-1">
                                          <b>Формула</b> - похожая по своей структуре на Excel ячейка, которая ведет пересчет по простым формулам. Доступны операции сложения, вычитания, умножения, деления.<br />
                                          Примеры формул: <em>=COL_3 * COL_4</em>, <em>=COL_3 * COL_4 + COL_5 / COL_3</em>, <em>=ЕСЛИ(COL_1<=350;350;COL_1)</em>
                                      </li>
                                  </ul>
                                </b-card>
                            </b-collapse>
                        </div>

                        <b-table-simple small bordered caption-top responsive :caption-top="false">
                            <b-thead>
                                <b-tr class="column-creation-head-tr">
                                    <b-th
                                        v-for="(item, index) in orderedColumns"
                                        :key="index"
                                        class="column-creation-head-th"
                                        :class="{ 'column-highlight': isColumnHighlighted(index) }"
                                    >
                                        <div class="ml-1 mb-0 pb-1">{{ index }}</div>
                                        <b-form-input
                                            v-model="form.columns[index].name"
                                            size="sm"
                                            :disabled="isUneditableNameOfColumnKeys(index)"
                                        ></b-form-input>  
                                    </b-th>
                                </b-tr>
                            </b-thead>
                            <b-tbody>
                                <b-tr>
                                    <b-td
                                        v-for="(item, index) in orderedColumns"
                                        :key="index"
                                        :class="{ 'column-highlight': isColumnHighlighted(index) }"
                                    >
                                        <div class="mb-2">
                                            <b-form-select
                                                v-model="form.columns[index].type"
                                                size="sm"
                                                :options="columnTypesByKey(index)"
                                                :disabled="isUneditableTypeOfColumnKeys(index)"
                                                @change="onColumnTypeChange(index)"
                                            ></b-form-select>
                                        </div>
                                        <div class="mb-2 position-relative">
                                            <b-form-textarea
                                                v-model="form.columns[index].default"
                                                size="sm"
                                                class="textarea-value"
                                                :disabled="form.columns[index].key == 'id'"
                                                @input="validateDefault(index)"
                                                @focus="highlightColumns(index)"
                                                @blur="clearHighlightedColumns"                                    
                                                trim
                                                rows="6"
                                            ></b-form-textarea>

                                            <div 
                                                v-if="form.columns[index].type === 'Формула'" 
                                                class="text-muted small formula-test"
                                            >
                                                {{ calculateFormulaExample(index) }}
                                            </div>                                
                                            <div 
                                                v-if="form.columns[index].type === 'Число'" 
                                                class="text-muted small formula-test"
                                            >
                                                <b-form-input
                                                    v-model="testValues[index]"
                                                    type="number"
                                                    step="0.01"
                                                    title="Введите тестовое значение для проверки формул"
                                                    placeholder="Тестовое значение"
                                                    size="sm"
                                                ></b-form-input>
                                            </div>                                
                                        </div>
                                        <div class="text-right">
                                            <b-button
                                                v-if="!isUndeletableColumn(index)"
                                                @click="deleteColumn(index)"
                                                variant="danger"
                                                size="sm"
                                            >
                                                <b-icon 
                                                    icon="trash-fill" 
                                                    scale="1" 
                                                    aria-label="Удалить"
                                                ></b-icon>
                                            </b-button>

                                            <b-button
                                                v-if="canMoveColumnLeft(index)"
                                                @click="moveColumnLeft(index)"
                                                variant="info"
                                                size="sm"
                                            >
                                                <b-icon 
                                                    icon="arrow-left" 
                                                    scale="1" 
                                                    aria-label="Передвинуть влево"
                                                ></b-icon>
                                            </b-button>

                                            <b-button
                                            v-if="canMoveColumnRight(index)"
                                            @click="moveColumnRight(index)"
                                            variant="info"
                                            size="sm"
                                            >
                                                <b-icon 
                                                    icon="arrow-right" 
                                                    scale="1" 
                                                    aria-label="Передвинуть вправо"
                                                ></b-icon>
                                            </b-button>
                                        </div>
                                    </b-td>
                                </b-tr>
                            </b-tbody>
                        </b-table-simple>
                    </b-form-group>
                </b-form>
                <div style="font-style: italic;">При перемещении колонок с формулами, не забудьте проверить их работу!</div>

                <template #modal-footer>
                    <div class="w-100 text-right">
                        <b-button
                            variant="secondary"
                            size="sm"
                            @click="showModal=false"
                        >
                            Закрыть
                        </b-button>
                        <b-button 
                            variant="primary"
                            @click="saveService"
                        >Сохранить</b-button>
                    </div>
                </template>          
            </b-modal>
            
        </template>
        <template v-if="isTemplatesActive">
            <CompanyFinanceInvoiceTemplateConstructor 
                :companyId="companyId"
                @updateTemplateActive="updateTemplateActive"
            />
        </template>
    </div>
  </template>
  
  <script>
  import { API_COMPANY_FINANCE_SERVICES } from '@/constants';
  import debounce from '@/services/helpers';
  import CompanyFinanceInvoiceTemplateConstructor from '@/components/companies/constructors/CompanyFinanceInvoiceTemplateConstructor';

  export default {
    name: "CompanyFinanceServiceConstructor",
    props: {
        companyId: {
            type: Number | String,
            required: true
        }
    },  
    components: {
        CompanyFinanceInvoiceTemplateConstructor
    },  
    data() {
      return {
        services: [],
        fields: [
            "id",
            { key: "name", label: "Наименование услуги" }, 
            { key: "actions", label: "Действия", class: ['w150px', 'text-center'] }
        ],
        showModal: false,
        loading: false,
        form: {
          id: null,
          company_id: null,
          name: "",
          columns: {
            COL_1: { key: "id", name: "ID", type: "ID", default: null, order: 1 },
            COL_2: {
              key: "name",
              name: "Наименование услуги",
              type: "Строка",
              default: "Введите наименование услуги",
              order: 2
            },
            COL_3: {
              key: "sum",
              name: "Итого",
              type: "Формула",
              default: "=1",
              order: 3
            }
          }
        },
        columnTypes: ["ID", "Строка", "Число", "Формула", "Дата"],
        undeletableColumnKeys: ["id", "name", "sum"],
        uneditableTypeOfColumnKeys: ["id", "name"],
        uneditableNameOfColumnKeys: ["id", "name", "sum"],
        highlightedColumns: [],
        testValues: {},
        isTemplatesActive: false,
        isShowFormulaError: false
      };
    },
    computed: {
      orderedColumns() {
        return Object.fromEntries(
           Object.entries(this.form.columns).sort(([, a], [, b]) => a.order - b.order)
        )
      },
    },
    watch: {
        form: {
            deep: true,
            handler() {
                this.calculateAllFormulas();
            }
        },
        isShowFormulaError: debounce(function (value) {
            if (this.isShowFormulaError) {
                this.$bvToast.toast(
                    'Обнаружены циклические зависимости в формулах. Пожалуйста, проверьте ваши формулы.', {
                    title: `Ошибка в формуле`,
                    variant: 'warning',
                    solid: true,
                });

                this.isShowFormulaError = false;
            }
        }, 500)              
    },
    mounted() {
        this.fetchServices();
    },
    methods: {
        fetchServices() {
            this.loading = true;
            api.call("get", `${API_COMPANY_FINANCE_SERVICES}?company_id=${this.companyId}`)
                .then((response) => {
                    this.services = response.data;
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        newService() {
            this.form = {
                id: null,
                company_id: this.companyId,
                name: "",
                columns: {
                    COL_1: { key: "id", name: "ID", type: "ID", default: null, order: 1 },
                    COL_2: {
                        key: "name",
                        name: "Наименование услуги",
                        type: "Строка",
                        default: "Введите наименование услуги",
                        order: 2
                    },
                    COL_3: {
                        key: "sum",
                        name: "Итого",
                        type: "Формула",
                        default: "=1",
                        order: 3
                    }
                }
            };

            this.addTestValues();

            this.showModal = true;
        },
        formatServiceColumns(columns) {
            return Object.values(columns).map((column) => ({
                name: column.name,
                default: column.default || "—",
            }));
        },
        // Заполняем тестовые значения по умолчанию для числовых колонок
        addTestValues() {
            this.testValues = {};

            Object.keys(this.form.columns).forEach((key) => {
                if (this.form.columns[key].type === "Число") {
                    this.$set(this.testValues, key, this.form.columns[key].default ? this.form.columns[key].default.replace(',', '.') : 0);
                }
            });            
        },
        editService(service) {
            this.form = JSON.parse(JSON.stringify(service));

            let namedColumns = {};

            this.form.columns.forEach((column) => {
                namedColumns[column.obj_key] = column;
                delete namedColumns[column.obj_key].obj_key;
            })
            
            this.form.columns = namedColumns;
            this.addTestValues();

            this.showModal = true;
        },
        cloneService(service) {
            let localService = JSON.parse(JSON.stringify(service));
            let namedColumns = {};

            localService.columns.forEach((column) => {
                namedColumns[column.obj_key] = column;
                delete namedColumns[column.obj_key].obj_key;
            })

            localService.columns = namedColumns;

            const clonedService = {
                ...localService,
                id: null,
                name: `Copy: ${localService.name}`,
            };

            this.loading = true;

            api.call("post", `${API_COMPANY_FINANCE_SERVICES}`, clonedService)
                .then(() => {
                    this.fetchServices();
                })
                .finally(() => {
                    this.loading = false;
                });
        },      
        deleteService(id) {
            this.loading = true;
            
            api.call("delete", `${API_COMPANY_FINANCE_SERVICES}/${id}`)
                .then(() => {
                    this.fetchServices();
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        saveService() {
            for (let key in this.form.columns) {
                if (this.form.columns[key].key === 'name') {
                    this.form.name = this.form.columns[key].default;
                }
            }

            if (!this.form.company_id) {
                this.form.company_id = this.companyId;
            }

            const method = this.form.id ? "put" : "post";
            const url = this.form.id
                ? `${API_COMPANY_FINANCE_SERVICES}/${this.form.id}`
                : `${API_COMPANY_FINANCE_SERVICES}`;

            this.loading = true; // Start loader
            api.call(method, url, this.form)
                .then(() => {
                    this.showModal = false;
                    this.fetchServices();
                })
                .finally(() => {
                    this.loading = false; // Stop loader
                });
        },
        addColumn() {
            const order = Object.keys(this.form.columns).length + 1;
            const key = `COL_${order}`;
            this.$set(this.form.columns, key, {
                key,
                name: "Новая колонка",
                type: "Строка",
                default: "",
                order
            });
        },
        deleteColumn(key) {
            if (!this.isUndeletableColumn(key)) {
                this.$delete(this.form.columns, key);
            }
        },
        isUndeletableColumn(key) {
            const column = this.form.columns[key];
            return column && this.undeletableColumnKeys.includes(column.key);
        },
        isUneditableTypeOfColumnKeys(key) {
            const column = this.form.columns[key];
            return column && this.uneditableTypeOfColumnKeys.includes(column.key);
        },
        isUneditableNameOfColumnKeys(key) {
            const column = this.form.columns[key];
            return column && this.uneditableNameOfColumnKeys.includes(column.key);
        },
        columnTypesByKey(key) {
            const column = this.form.columns[key];
            if (column && column.key === 'sum') {
                return ["Число", "Формула"];
            } else {
                return this.columnTypes;
            }
        },
        canMoveColumnLeft(key) {
            const keys = Object.keys(this.orderedColumns);
            const index = keys.indexOf(key);
            return index > 0;
        },
        canMoveColumnRight(key) {
            const keys = Object.keys(this.orderedColumns);
            const index = keys.indexOf(key);
            return index < keys.length - 1;
        },
        moveColumnLeft(key) {
            const keys = Object.keys(this.orderedColumns);
            const index = keys.indexOf(key);
            if (index > 0) {
                const prevKey = keys[index - 1];
                // this.swapColumns(key, prevKey);
                // this.reorderingMainKeysOfColumns();
                this.updateFormulasAfterMove(key, prevKey);
            }
        },
        moveColumnRight(key) {
            const keys = Object.keys(this.orderedColumns);
            const index = keys.indexOf(key);
            if (index < keys.length - 1) {
                const nextKey = keys[index + 1];
                // this.swapColumns(key, nextKey);
                // this.reorderingMainKeysOfColumns();
                this.updateFormulasAfterMove(key, nextKey);
            }
        },
        updateFormulasAfterMove(movedKey, targetKey) {
            // console.log("Текущие колонки:", this.form.columns);
            const columns = this.form.columns;

            // Убедитесь, что ключи существуют
            if (!columns[movedKey] || !columns[targetKey]) {
                console.error(`Колонка с ключом ${movedKey} или ${targetKey} отсутствует.`);
                return;
            }

            // Меняем порядок колонок
            this.swapColumns(movedKey, targetKey);

            // Обновляем формулы
            Object.keys(columns).forEach((key) => {
                const column = columns[key];
                if (column && column.type === "Формула") {
                    // Проверяем наличие formula и обновляем
                    column.default = this.replaceColumnReferences(column.default, movedKey, targetKey);
                }
            });

            this.reorderingMainKeysOfColumns();

            // Пересчитываем формулы
            this.calculateAllFormulas();
        },
        replaceColumnReferences(formula, movedKey, targetKey) {
            if (!formula) {
                console.warn("Пустая формула передана в replaceColumnReferences.");
                return "";
            }

            const movedRegex = new RegExp(`\\b${movedKey}\\b`, "g");
            const targetRegex = new RegExp(`\\b${targetKey}\\b`, "g");

            const updatedFormula = formula
                .replace(movedRegex, `__SWAP__`)
                .replace(targetRegex, movedKey)
                .replace(/__SWAP__/g, targetKey);

            // Check if any placeholder remains
            if (updatedFormula.includes("__SWAP__")) {
                console.error("Ошибка замены ссылок в формуле:", updatedFormula);
            }

            return updatedFormula;
        },
        swapColumns(keyA, keyB) {
            const tempOrder = this.form.columns[keyA].order;
            this.form.columns[keyA].order = this.form.columns[keyB].order;
            this.form.columns[keyB].order = tempOrder;
        },
        reorderingMainKeysOfColumns() {
            const transformedObj = Object.keys(this.orderedColumns).reduce((acc, key, index) => {
                acc[`COL_${index + 1}`] = this.orderedColumns[key];

                if (!this.undeletableColumnKeys.includes(this.orderedColumns[key].key)) {
                    acc[`COL_${index + 1}`].key = `COL_${index + 1}`;
                }

                return acc;
            }, {});

            this.$set(this.form, 'columns', transformedObj);
        },
        /**
         * Пересчитывает все формулы с учетом зависимостей.
         */
         calculateAllFormulas(affectedColumns = []) {
            const columns = this.form.columns;
            const dependencies = {};
            const calculatedValues = { ...this.testValues }; // Use test values for formula calculations

            // Determine the dependencies for the formulas
            Object.keys(columns).forEach((key) => {
                if (columns[key].type === "Формула") {
                    const formula = columns[key].default.replace(/;/g, ",");
                    const references = formula.match(/COL_\d+/g) || [];
                    dependencies[key] = { formula, references };
                }
            });

            // Perform topological sorting
            const sortedKeys = this.topologicalSort(dependencies);

            // If there was an error in sorting, prevent calculation
            if (this.errorOccurred) return;

            // Only recalculate affected columns or all columns if none were specified
            const keysToCalculate = affectedColumns.length ? affectedColumns : sortedKeys;

            // Calculate the formulas
            keysToCalculate.forEach((key) => {
                const { formula, references } = dependencies[key];

                try {
                    // Replace references with their values
                    let parsedFormula = formula.replace(/COL_\d+/g, (match) => {
                        if (calculatedValues[match] !== undefined) {
                            return calculatedValues[match];
                        }
                        throw new Error(`Значение для ${match} отсутствует`);
                    });

                    // Replace ЕСЛИ with ternary operator
                    parsedFormula = parsedFormula.replace(
                        /ЕСЛИ\(([^,]+),([^,]+),([^)]+)\)/g,
                        "($1 ? $2 : $3)"
                    );

                    // Calculate the result
                    const result = this.safeEvaluate(parsedFormula.replace("=", ""));
                    calculatedValues[key] = result;
                } catch (error) {
                    console.error(`Ошибка в формуле ${key}: ${error.message}`);
                    calculatedValues[key] = `Ошибка: ${error.message}`;
                }
            });

            // Update the calculated results for formulas
            Object.keys(columns).forEach((key) => {
                if (columns[key].type === "Формула") {
                    columns[key].calculatedExample = (typeof calculatedValues[key] === 'number') 
                        ? calculatedValues[key].toFixed(2).replace(',', '.') 
                        : calculatedValues[key];
                }
            });
        },

        /**
         * Выполняет топологическую сортировку для определения порядка вычисления формул.
         */
         topologicalSort(dependencies) {
            const visited = new Set();
            const sorted = [];
            const visiting = new Set();
            let hasError = false; // Flag to track errors during sorting

            const visit = (key) => {
                if (visiting.has(key)) {
                    // If we find a cycle, mark the error and don't stop everything
                    console.error(`Циклическая зависимость обнаружена в формуле ${key}`);
                    hasError = true;
                    return; // Do not proceed with this key
                }
                if (!visited.has(key)) {
                    visiting.add(key);
                    const { references } = dependencies[key];
                    references.forEach((ref) => {
                        if (dependencies[ref]) {
                            visit(ref);
                        }
                    });
                    visiting.delete(key);
                    visited.add(key);
                    sorted.push(key);
                }
            };

            Object.keys(dependencies).forEach((key) => {
                if (!visited.has(key)) {
                    visit(key);
                }
            });

            if (hasError) {
                this.isShowFormulaError = true;
            }

            return sorted;
        },

        /**
         * Вычисляет пример значения для формулы.
         */
        calculateFormulaExample(key) {
            this.calculateAllFormulas(); // Пересчитываем все формулы
            const column = this.form.columns[key];
            return column.calculatedExample
                ? `Пример расчёта: ${column.calculatedExample}`
                : "Ошибка в формуле";
        },

        validateDefault(key) {
            const column = this.form.columns[key];

            if (column.type === "Формула") {
                const formula = column.default;
                // Check if formula contains only uppercase letters, numbers, and valid references
                const isValid = /^[A-Z0-9_=\(\)\+\-\*/.]*$/.test(formula);
                if (!isValid) {
                    column.default = formula.toUpperCase(); // Automatically convert to uppercase
                }
            } else if (column.type === "Число") {
                let value = column.default;

                column.default = value.replace(/,/g, ".")
                    .replace(/[^.\d]/g, '')
                    .replace(/^(\d*\.?)|(\d*)\.?/g, "$1$2");

                // Initialize test value if not set
                if (!this.testValues[key]) {
                    this.$set(this.testValues, key, parseFloat(column.default) || 0);
                }
            }
        },
        safeEvaluate(expression) {
            // Allow only numbers, operators, parentheses, and whitespace
            if (/^[\d\s\+\-\*\/\(\)\?\.:<>=]+$/.test(expression)) {
                return Function(`"use strict"; return (${expression});`)();
            }
            throw new Error("Недопустимые символы в формуле");
        },
        highlightColumns(key) {
            const formula = this.form.columns[key].default;

            if (formula && formula.startsWith("=")) {
                // Extract column references (e.g., COL_1, COL_2)
                const references = formula.match(/COL_\d+/g);
                if (references) {
                    this.highlightedColumns = references;
                }
            }
        },
        clearHighlightedColumns() {
            this.highlightedColumns = [];
        },
        isColumnHighlighted(key) {
            return this.highlightedColumns.includes(key);
        },
        onColumnTypeChange(key) {
            const column = this.form.columns[key];
            
            // Если тип изменился на "Число", преобразуем значение по умолчанию
            if (column.type === "Число" && column.default) {
                // Преобразуем запятую в точку и убираем все нечисловые символы кроме точки
                let value = column.default.toString();
                value = value.replace(/,/g, ".")
                    .replace(/[^.\d]/g, '')
                    .replace(/^(\d*\.?)|(\d*)\.?/g, "$1$2");
                
                column.default = value;
                
                // Инициализируем тестовое значение
                this.$set(this.testValues, key, parseFloat(value) || 0);
            }
        },
        updateTemplateActive(value) {
            this.isTemplatesActive = value;
        }

  },
};
</script>

<style scoped>
.column-creation-head-tr {
    background: #efefef;
}
.column-creation-head-th {
    min-width: 200px;
    padding-top: 7px !important;
    padding-bottom: 7px !important; 
}
.textarea-value {
    line-height: 16px;
}
.loading-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(255, 255, 255, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1050;
}
.formula-test {
    position: absolute;
    left: 0;
    bottom: 0;
    background: #F1F1F1;
    padding: 3px;
}
.formula-test input.form-control-sm {
    font-size: 11px;
    padding: 0px 2px;
    line-height: 17px;
    max-width: 125px;
}
.column-highlight {
    background-color: #ffeb3b44;
    transition: background-color 0.3s;
}
</style>